import { useEffect, useRef, useState } from 'react';

import { Boton } from './componentes';

/**
 * Buscar un insumo apuntándole con algo, en vez de tecleando.
 *
 * Contar doscientos insumos en una tablet, escribiendo, es lento y se equivoca:
 * la fila que se busca está a treinta pantallas de distancia y el dedo cae en
 * la de al lado. Con esto se apunta al envase y el cursor cae solo en la
 * casilla correcta.
 *
 * **Dos lectores, y el importante es el barato.** La mayoría de locales usan un
 * lector USB de treinta soles que el sistema ve como un teclado: escribe el
 * código muy rápido y manda un Enter. Eso funciona aquí sin nada especial,
 * porque este campo es un campo de texto. La cámara es la segunda opción, para
 * la tablet del depósito que no tiene lector colgando.
 *
 * **`BarcodeDetector` y no una librería.** Chrome y Android lo traen; iOS no.
 * Meter un lector de códigos en JavaScript son cientos de kilobytes que
 * descargarían también los locales que usan el lector USB, para nada. Cuando no
 * está, el botón de cámara no aparece y el campo sigue funcionando igual.
 */
export function Escaner({
  onCodigo,
  etiqueta = 'Escanear o teclear el código',
}: {
  onCodigo: (codigo: string) => void;
  etiqueta?: string;
}) {
  const [texto, setTexto] = useState('');
  const [camara, setCamara] = useState(false);
  const [fallo, setFallo] = useState<string | null>(null);
  const campo = useRef<HTMLInputElement>(null);

  const hayCamara =
    typeof window !== 'undefined' && 'BarcodeDetector' in window;

  return (
    <div className="escaner">
      <form
        className="escaner__campo"
        onSubmit={(evento) => {
          evento.preventDefault();
          if (texto.trim() === '') return;

          onCodigo(texto.trim());
          setTexto('');

          // El foco se queda aquí: quien cuenta pasa un envase tras otro y
          // devolvérselo al campo es lo que hace que no toque la pantalla.
          campo.current?.focus();
        }}
      >
        <input
          ref={campo}
          className="buscador escaner__entrada"
          type="text"
          inputMode="text"
          autoComplete="off"
          aria-label={etiqueta}
          placeholder={etiqueta}
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
        />
        {hayCamara ? (
          <Boton onClick={() => setCamara((previo) => !previo)}>
            {camara ? 'Cerrar cámara' : 'Usar cámara'}
          </Boton>
        ) : null}
      </form>

      {fallo === null ? null : <p className="nota-sm nota--pegada">{fallo}</p>}

      {camara ? (
        <CamaraLectora
          onCodigo={(codigo) => {
            onCodigo(codigo);
            campo.current?.focus();
          }}
          onFallo={(mensaje) => {
            setFallo(mensaje);
            setCamara(false);
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * La cámara leyendo códigos, mientras esté abierta.
 *
 * Se apaga sola al cerrarse —y al salir de la pantalla— porque una cámara que
 * se queda encendida detrás gasta batería y enciende la luz del equipo, que es
 * lo que hace que alguien tape el lente con cinta y no lo vuelva a usar.
 *
 * No repite el mismo código dos veces seguidas: un envase delante del lente se
 * lee treinta veces por segundo, y sin esto una lata contaría treinta.
 */
function CamaraLectora({
  onCodigo,
  onFallo,
}: {
  onCodigo: (codigo: string) => void;
  onFallo: (mensaje: string) => void;
}) {
  const video = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    let vigente = true;
    let stream: MediaStream | null = null;
    let temporizador: number | undefined;
    let ultimo = '';

    const arrancar = async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          // La de atrás: quien cuenta apunta al estante, no a su cara.
          video: { facingMode: 'environment' },
        });

        if (!vigente) {
          for (const pista of stream.getTracks()) pista.stop();

          return;
        }

        if (video.current !== null) {
          video.current.srcObject = stream;
          await video.current.play();
        }

        const Detector = (
          window as unknown as {
            BarcodeDetector: new (options?: {
              formats?: string[];
            }) => { detect(source: CanvasImageSource): Promise<{ rawValue: string }[]> };
          }
        ).BarcodeDetector;

        const detector = new Detector({
          formats: ['ean_13', 'ean_8', 'code_128', 'code_39', 'upc_a', 'upc_e'],
        });

        const mirar = async () => {
          if (!vigente || video.current === null) return;

          try {
            const encontrados = await detector.detect(video.current);
            const codigo = encontrados[0]?.rawValue;

            if (codigo !== undefined && codigo !== ultimo) {
              ultimo = codigo;
              onCodigo(codigo);
            } else if (encontrados.length === 0) {
              // Al quitar el envase se olvida el último, para que el mismo
              // producto se pueda volver a contar en la siguiente pasada.
              ultimo = '';
            }
          } catch {
            // Un fotograma que no se pudo leer no es un error: el siguiente sí.
          }

          temporizador = window.setTimeout(() => void mirar(), 250);
        };

        void mirar();
      } catch {
        onFallo(
          'No se pudo abrir la cámara. Revisa el permiso del navegador, o usa un lector USB: escribe en el campo como si fuera un teclado.',
        );
      }
    };

    void arrancar();

    return () => {
      vigente = false;
      window.clearTimeout(temporizador);
      if (stream !== null) {
        for (const pista of stream.getTracks()) pista.stop();
      }
    };
  }, [onCodigo, onFallo]);

  return (
    <div className="escaner__camara">
      <video ref={video} muted playsInline />
      <p className="nota-sm nota--pegada">
        Apunta al código del envase. Se cuenta uno por lectura.
      </p>
    </div>
  );
}
